import type { FC, ReactNode } from 'react';
import { createContext, useContext, useEffect } from 'react';

import { useKnockClient, useNotificationStore, useNotifications } from '@knocklabs/react';

import { KNOCK_FEED_CHANNEL_ID } from './constants';

type FeedClient = ReturnType<typeof useNotifications>;

type FeedItemOrItems = Parameters<FeedClient['markAsRead']>[0];
export type FeedItem = Exclude<FeedItemOrItems, unknown[]>;

type NotificationFeedContextValue = {
  feedClient: FeedClient;
  unreadCount: number;
};

const NotificationFeedContext = createContext<NotificationFeedContextValue | null>(null);

/**
 * The feed, or null where there is none: the provider is only mounted behind the Knock flag, and only
 * for a user Knock has issued a token to.
 */
export const useOptionalNotificationFeed = (): NotificationFeedContextValue | null =>
  useContext(NotificationFeedContext);

export const useNotificationFeed = (): NotificationFeedContextValue => {
  const value = useOptionalNotificationFeed();
  if (value == null) {
    throw new Error('useNotificationFeed must be used within NotificationFeedProvider');
  }
  return value;
};

export const useUnreadNotificationCount = (): number => useOptionalNotificationFeed()?.unreadCount ?? 0;

type NotificationFeedProviderProps = {
  children: ReactNode;
};

export const NotificationFeedProvider: FC<NotificationFeedProviderProps> = ({ children }) => {
  const knockClient = useKnockClient();
  const feedClient = useNotifications(knockClient, KNOCK_FEED_CHANNEL_ID);
  const { metadata } = useNotificationStore(feedClient);

  useEffect(() => {
    feedClient.fetch();
    feedClient.listenForUpdates();

    return () => {
      feedClient.dispose();
    };
  }, [feedClient]);

  const value: NotificationFeedContextValue = {
    feedClient,
    unreadCount: metadata.unread_count,
  };

  return <NotificationFeedContext.Provider value={value}>{children}</NotificationFeedContext.Provider>;
};
